import { lazy, Suspense } from 'react';
import { Outlet, createBrowserRouter, Navigate } from 'react-router-dom';
import { RouteErrorBoundary } from '@/routes/RouteErrorBoundary';
import { RouteFallback } from '@/routes/RouteFallback';
import { useAuth } from '@/store/auth';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import type { Profile } from '@/api/types/common';

// Auth flow + the admin shell stay eager: they are the first paint for new
// and returning visitors respectively, so code-splitting them only adds a
// network waterfall before anything renders. Everything below AdminLayout is
// lazily loaded — a returning admin downloads just the dashboard chunk first,
// and each feature area arrives on demand (Suspense boundary lives in
// AdminLayout's <Outlet>, so the chrome stays put while a page loads).
import LoginPage from '@/pages/auth/Login';
import OtpVerifyPage from '@/pages/auth/OtpVerify';
import ProfileSelectPage from '@/pages/auth/ProfileSelect';
import UnsupportedRolePage from '@/pages/auth/UnsupportedRole';
import AcademyPausedPage from '@/pages/auth/AcademyPaused';
import AdminLayout from '@/routes/AdminLayout';

const PaymentResult = lazy(() => import('@/pages/PaymentResult'));
const DashboardPage = lazy(() => import('@/pages/admin/Dashboard'));
const BranchesIndex = lazy(() => import('@/pages/admin/branches/Index'));
const BranchDetail = lazy(() => import('@/pages/admin/branches/Detail'));
const BranchForm = lazy(() => import('@/pages/admin/branches/Form'));
const StudentsIndex = lazy(() => import('@/pages/admin/students/Index'));
const StudentDetail = lazy(() => import('@/pages/admin/students/Detail'));
const StudentEditForm = lazy(() => import('@/pages/admin/students/EditForm'));
const StudentCreateForm = lazy(() => import('@/pages/admin/students/Form'));
const StudentAttendance = lazy(() => import('@/pages/admin/students/Attendance'));
const StudentFeeHistory = lazy(() => import('@/pages/admin/students/FeeHistory'));
const BatchesIndex = lazy(() => import('@/pages/admin/batches/Index'));
const BatchDetail = lazy(() => import('@/pages/admin/batches/Detail'));
const BatchEditForm = lazy(() => import('@/pages/admin/batches/EditForm'));
const BatchForm = lazy(() => import('@/pages/admin/batches/Form'));
const TrainersIndex = lazy(() => import('@/pages/admin/trainers/Index'));
const TrainerDetail = lazy(() => import('@/pages/admin/trainers/Detail'));
const TrainerForm = lazy(() => import('@/pages/admin/trainers/Form'));
const ManagersIndex = lazy(() => import('@/pages/admin/managers/Index'));
const ManagerDetail = lazy(() => import('@/pages/admin/managers/Detail'));
const ManagerForm = lazy(() => import('@/pages/admin/managers/Form'));
const AttendanceIndex = lazy(() => import('@/pages/admin/attendance/Index'));
const TodaysClassesIndex = lazy(() => import('@/pages/admin/todays-classes/Index'));
const FeeDetail = lazy(() => import('@/pages/admin/fees/FeeDetail'));
const RecentlyConductedClassesIndex = lazy(
  () => import('@/pages/admin/recently-conducted-classes/Index'),
);
const BatchAttendance = lazy(() => import('@/pages/admin/attendance/BatchAttendance'));
const LeadsIndex = lazy(() => import('@/pages/admin/leads/Index'));
const LeadDetail = lazy(() => import('@/pages/admin/leads/Detail'));
const LeadCreate = lazy(() => import('@/pages/admin/leads/Create'));
const LeadEdit = lazy(() => import('@/pages/admin/leads/Edit'));
const LeadBoard = lazy(() => import('@/pages/admin/leads/Board'));
const LeadWorkQueue = lazy(() => import('@/pages/admin/leads/WorkQueue'));
const LeadAnalytics = lazy(() => import('@/pages/admin/leads/Analytics'));
const FeesIndex = lazy(() => import('@/pages/admin/fees/Index'));
const OtherFeesIndex = lazy(() => import('@/pages/admin/other-fees/Index'));
const SalaryIndex = lazy(() => import('@/pages/admin/salary/Index'));
const SettlementsIndex = lazy(() => import('@/pages/admin/settlements/Index'));
const PaymentsIndex = lazy(() => import('@/pages/admin/payments/Index'));
const CalendarPage = lazy(() => import('@/pages/admin/calendar/Index'));
const NotificationsPage = lazy(() => import('@/pages/admin/notifications/Index'));
const MembershipPage = lazy(() => import('@/pages/admin/membership/Index'));
const ProfilePage = lazy(() => import('@/pages/admin/profile/Index'));

// Root redirect: send authenticated admins to the dashboard, everyone else
// to login. Reads from the persisted auth store so a returning visitor with a
// valid token skips the login page entirely.
const APP_ROLES = ['admin', 'manager', 'trainer'] as const;

function RootRedirect() {
  const token = useAuth((s) => s.token);
  const user = useAuth((s) => s.user);
  const profile = useAuth((s) => s.profile);
  // Either Bearer (legacy) or cookie-auth (user set by login). Cookie itself
  // is not JS-readable; user object is our proxy for "session is live".
  const role = profile?.role;
  if ((token || user) && role && (APP_ROLES as readonly string[]).includes(role)) {
    return <Navigate to="/admin" replace />;
  }
  return <Navigate to="/login" replace />;
}

// Per-route role allowlist. A profile whose role is NOT in `allow` is bounced
// back to the dashboard. This is an ALLOWLIST (not a manager blocklist): with
// three web roles, "not manager" no longer implies "admin", so every gated
// route must name exactly the roles permitted. Backend enforces the same via
// role middleware — this only avoids rendering a page that would 403.
function RequireRole({
  allow,
  children,
}: {
  allow: Array<Profile['role']>;
  children: React.ReactNode;
}) {
  const profile = useAuth((s) => s.profile);
  if (profile && !allow.includes(profile.role)) return <Navigate to="/admin" replace />;
  return <>{children}</>;
}

// Convenience wrappers for the two common gates.
const AdminOnly = ({ children }: { children: React.ReactNode }) => (
  <RequireRole allow={['admin']}>{children}</RequireRole>
);
const AdminOrManager = ({ children }: { children: React.ReactNode }) => (
  <RequireRole allow={['admin', 'manager']}>{children}</RequireRole>
);

// Wrap every route in a single parent so a thrown render/loader error in
// any child surfaces our RouteErrorBoundary instead of React Router's
// default dev message.
export const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <RouteErrorBoundary />,
    children: [
  { path: '/', element: <RootRedirect /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/login/otp', element: <OtpVerifyPage /> },
  { path: '/login/profile', element: <ProfileSelectPage /> },
  { path: '/unsupported-role', element: <UnsupportedRolePage /> },
  // Outside the protected tree on purpose: a paused academy's session is
  // still valid, but every data route behind AdminLayout 423s.
  { path: '/academy-paused', element: <AcademyPausedPage /> },
  {
    path: '/payment/success',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <PaymentResult outcome="success" />
      </Suspense>
    ),
  },
  {
    path: '/payment/failure',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <PaymentResult outcome="failure" />
      </Suspense>
    ),
  },

  {
    path: '/admin',
    element: (
      <ProtectedRoute roles={['admin', 'manager', 'trainer']}>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'branches', element: <AdminOrManager><BranchesIndex /></AdminOrManager> },
      { path: 'branches/new', element: <AdminOnly><BranchForm mode="create" /></AdminOnly> },
      { path: 'branches/:id', element: <AdminOrManager><BranchDetail /></AdminOrManager> },
      { path: 'branches/:id/edit', element: <AdminOnly><BranchForm mode="edit" /></AdminOnly> },
      { path: 'batches', element: <BatchesIndex /> },
      { path: 'batches/new', element: <AdminOrManager><BatchForm /></AdminOrManager> },
      { path: 'batches/:id', element: <BatchDetail /> },
      { path: 'batches/:id/edit', element: <BatchEditForm /> },
      { path: 'students', element: <StudentsIndex /> },
      { path: 'students/new', element: <StudentCreateForm /> },
      { path: 'students/:id', element: <StudentDetail /> },
      { path: 'students/:id/edit', element: <StudentEditForm /> },
      { path: 'students/:id/fees', element: <StudentFeeHistory /> },
      { path: 'students/:id/batch/:batchId/attendance', element: <StudentAttendance /> },
      { path: 'trainers', element: <AdminOrManager><TrainersIndex /></AdminOrManager> },
      { path: 'trainers/new', element: <AdminOrManager><TrainerForm mode="create" /></AdminOrManager> },
      { path: 'trainers/:id', element: <AdminOrManager><TrainerDetail /></AdminOrManager> },
      { path: 'trainers/:id/edit', element: <AdminOrManager><TrainerForm mode="edit" /></AdminOrManager> },
      { path: 'managers', element: <AdminOnly><ManagersIndex /></AdminOnly> },
      { path: 'managers/new', element: <AdminOnly><ManagerForm mode="create" /></AdminOnly> },
      { path: 'managers/:id', element: <AdminOnly><ManagerDetail /></AdminOnly> },
      { path: 'managers/:id/edit', element: <AdminOnly><ManagerForm mode="edit" /></AdminOnly> },
      { path: 'leads', element: <LeadsIndex /> },
      { path: 'leads/new', element: <LeadCreate /> },
      // Declared before `leads/:id` so the static segments read as distinct
      // pages rather than lead ids.
      { path: 'leads/board', element: <LeadBoard /> },
      { path: 'leads/work-queue', element: <LeadWorkQueue /> },
      { path: 'leads/analytics', element: <LeadAnalytics /> },
      { path: 'leads/:id', element: <LeadDetail /> },
      { path: 'leads/:id/edit', element: <LeadEdit /> },
      { path: 'attendance', element: <AttendanceIndex /> },
      // Scoped server-side for every role — see rpath.dailyClasses.
      { path: 'todays-classes', element: <TodaysClassesIndex /> },
      { path: 'attendance/:batchId', element: <BatchAttendance /> },
      // Managers and trainers get their own scoped copy of this list.
      { path: 'recently-conducted-classes', element: <RecentlyConductedClassesIndex /> },
      { path: 'fees', element: <FeesIndex /> },
      { path: 'other-fees', element: <OtherFeesIndex /> },
      { path: 'admission-fees', element: <Navigate to="/admin/other-fees" replace /> },
      // :type is batch | admission — the two money-in invoice kinds a payment
      // row can point at. Scope-checked server-side.
      { path: 'fees/:type/:feeId', element: <FeeDetail /> },
      { path: 'salary', element: <SalaryIndex /> },
      // Open to all three web roles: the page itself hides the admin-only payout
      // tab and shows managers/trainers just their scoped invoice view.
      { path: 'settlements', element: <SettlementsIndex /> },
      { path: 'payments', element: <AdminOnly><PaymentsIndex /></AdminOnly> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'notifications', element: <NotificationsPage /> },
      { path: 'membership', element: <AdminOnly><MembershipPage /></AdminOnly> },
      { path: 'profile', element: <ProfilePage /> },
    ],
  },

  { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
], {
  // Opt in to v7 behaviours early to silence dev warnings and surface any
  // breakage before we upgrade. v7_startTransition lives on RouterProvider
  // (set in App.tsx); the rest are router-level flags.
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});
